<template>
  <div class="modal-backdrop" @click.self="$emit('close')" @keydown.esc="$emit('close')">
    <section class="modal-panel modal-panel-wide" role="dialog" aria-modal="true" :aria-labelledby="titleId">
      <div class="history-dialog">
        <div class="history-rev-list">
          <button
            v-for="entry in history?.entries ?? []"
            :key="entry.id"
            type="button"
            :class="['history-rev', { active: entry.id === selectedEntryId }]"
            @click="selectedEntryId = entry.id"
          >
            <div class="history-rev-top">
              <span class="history-rev-time">{{ formatInstant(entry.createdAt) }}</span>
              <span class="history-stats">
                <span class="diff-stat diff-stat-added">+{{ Number(entry.addedLineCount) }}</span>
                <span class="diff-stat diff-stat-removed">−{{ Number(entry.deletedLineCount) }}</span>
              </span>
            </div>
            <span class="history-rev-actor">{{ entry.actorName ?? 'Unknown' }}</span>
            <span v-if="wasRenamed(entry)" class="history-rename">
              <Pencil class="icon" />
              <span><s>{{ entry.previousTitle }}</s> → {{ entry.title }}</span>
            </span>
          </button>
        </div>

        <div class="history-detail-pane">
          <header class="history-detail-bar">
            <h2 :id="titleId" class="modal-title">History · {{ document.title }}</h2>
            <button type="button" class="icon-button modal-close" aria-label="Close" @click="$emit('close')">
              <X class="icon" />
            </button>
          </header>
          <div class="history-detail-content">
            <div v-if="error" class="workspace-alert">{{ error }}</div>
            <div v-if="history == null" class="history-dialog-loading">Loading history…</div>
            <div v-else-if="history.entries.length === 0" class="history-dialog-loading">No history yet.</div>
            <template v-else-if="selectedEntry">
              <div class="history-detail-head">
                <div>
                  <p class="history-detail-title">{{ formatInstant(selectedEntry.createdAt) }}</p>
                  <p class="history-detail-sub">{{ selectedEntry.actorName ?? 'Unknown' }} · compared to previous revision</p>
                  <span v-if="wasRenamed(selectedEntry)" class="history-rename">
                    <Pencil class="icon" />
                    <span>Renamed <s>{{ selectedEntry.previousTitle }}</s> → {{ selectedEntry.title }}</span>
                  </span>
                </div>
                <div class="history-detail-actions">
                  <button
                    type="button"
                    class="btn"
                    :disabled="!canShowChanges(selectedEntry)"
                    @click="showChanges(selectedEntry)"
                  >
                    <FileDiff class="icon" />
                    <span>Show changes</span>
                  </button>
                  <button
                    v-if="document.canRestoreHistory"
                    type="button"
                    class="btn"
                    :disabled="restoring"
                    @click="confirmingRestore = selectedEntry"
                  >
                    <RotateCcw class="icon" />
                    <span>Restore</span>
                  </button>
                </div>
              </div>
              <div v-if="diffErrors[selectedEntry.id]" class="workspace-alert">{{ diffErrors[selectedEntry.id] }}</div>
              <div v-if="loadingDiffs.has(selectedEntry.id)" class="history-dialog-loading">Loading diff…</div>
              <UnifiedDiff
                v-else-if="entryDiffs[selectedEntry.id]"
                :hunks="entryDiffs[selectedEntry.id].diffHunks"
                :diff-available="entryDiffs[selectedEntry.id].diffAvailable"
              />
            </template>
          </div>
        </div>
      </div>
    </section>
  </div>
  <ConfirmDialog
    v-if="confirmingRestore"
    title="Restore revision"
    :message="`Restore this document to the version from ${formatInstant(confirmingRestore.createdAt)}? The current content will be replaced.`"
    confirm-label="Restore"
    @confirm="restore(confirmingRestore)"
    @cancel="confirmingRestore = null"
  />
</template>

<script setup lang="ts">
import { FileDiff, Pencil, RotateCcw, X } from 'lucide-vue-next';
import { computed, onMounted, reactive, ref, useId, watch } from 'vue';
import type { EntityId } from '../api/entityId';
import {
  CommandsService,
  QueriesService,
  type DocumentHistoryEntryResponse,
  type GetDocumentHistoryEntryDiffQueryResponse,
  type GetDocumentHistoryQueryResponse,
  type GetDocumentQueryResponse
} from '../api/generated';
import { formatInstant } from '../format';
import { getErrorMessage } from '../stores/session';
import ConfirmDialog from './ConfirmDialog.vue';
import UnifiedDiff from './UnifiedDiff.vue';

const props = defineProps<{
  document: GetDocumentQueryResponse;
}>();

const emit = defineEmits<{
  close: [];
  showChanges: [documentId: EntityId, historyEntryId: EntityId, baselineContentMarkdown: string];
  restored: [document: GetDocumentQueryResponse];
}>();

const titleId = useId();
const history = ref<GetDocumentHistoryQueryResponse | null>(null);
const selectedEntryId = ref<EntityId | null>(null);
const error = ref<string | null>(null);
const entryDiffs = reactive<Record<EntityId, GetDocumentHistoryEntryDiffQueryResponse>>({});
const diffErrors = reactive<Record<EntityId, string>>({});
const loadingDiffs = reactive(new Set<EntityId>());
const restoring = ref(false);
const confirmingRestore = ref<DocumentHistoryEntryResponse | null>(null);

const selectedEntry = computed(() =>
  history.value?.entries.find(entry => entry.id === selectedEntryId.value) ?? null);

function wasRenamed(entry: DocumentHistoryEntryResponse): boolean {
  return entry.previousTitle != null
    && entry.previousTitle.length > 0
    && entry.previousTitle !== entry.title;
}

onMounted(async () => {
  try {
    history.value = await QueriesService.queryGetDocumentHistoryQuery({
      requestBody: { documentId: props.document.id }
    });
    selectedEntryId.value = history.value.entries[0]?.id ?? null;
  } catch (caught) {
    error.value = getErrorMessage(caught, 'Unable to load history.');
  }
});

watch(selectedEntryId, id => {
  if (id != null) {
    void loadDiff(id);
  }
});

async function loadDiff(historyEntryId: EntityId): Promise<void> {
  if (entryDiffs[historyEntryId] != null || loadingDiffs.has(historyEntryId)) {
    return;
  }

  loadingDiffs.add(historyEntryId);
  delete diffErrors[historyEntryId];
  try {
    entryDiffs[historyEntryId] = await QueriesService.queryGetDocumentHistoryEntryDiffQuery({
      requestBody: { historyEntryId }
    });
  } catch (caught) {
    diffErrors[historyEntryId] = getErrorMessage(caught, 'Unable to load diff.');
  } finally {
    loadingDiffs.delete(historyEntryId);
  }
}

function canShowChanges(entry: DocumentHistoryEntryResponse): boolean {
  return entryDiffs[entry.id] != null
    && !loadingDiffs.has(entry.id)
    && diffErrors[entry.id] == null;
}

function showChanges(entry: DocumentHistoryEntryResponse): void {
  const diff = entryDiffs[entry.id];
  if (diff == null || !canShowChanges(entry)) {
    return;
  }

  emit('showChanges', props.document.id, entry.id, diff.oldFile.content);
}

async function restore(entry: DocumentHistoryEntryResponse): Promise<void> {
  confirmingRestore.value = null;
  restoring.value = true;
  error.value = null;
  try {
    const document = await CommandsService.commandRestoreDocumentHistoryCommand({ requestBody: { historyEntryId: entry.id } });
    emit('restored', document);
  } catch (caught) {
    error.value = getErrorMessage(caught, 'Unable to restore history entry.');
  } finally {
    restoring.value = false;
  }
}
</script>
